/**
 * Centralized schema generation for all structured data
 * Combines functionality from structured-data.js, schema-generator.ts, and schema-manager.ts
 */

// ============================================
// BUSINESS & ORGANIZATION SCHEMAS
// ============================================

export function generateOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "behes.dev",
    legalName: "behes.dev",
    url: "https://behes.dev",
    logo: "https://behes.dev/favicon.png",
    image: "https://behes.dev/preview.png",
    description:
      "Arthur Behesnilian, web designer building custom websites for small businesses and service brands. Offering One-Page Landing Page, Custom Website, and Managed Hosting services. Serving Pasadena and clients nationwide.",
    foundingDate: "2025",
    founders: [
      {
        "@type": "Person",
        name: "Arthur Behesnilian",
      },
    ],
    address: {
      "@type": "PostalAddress",
      addressLocality: "Pasadena",
      addressRegion: "CA",
      postalCode: "91104",
      addressCountry: "US",
    },
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Customer Service",
      areaServed: ["US", "Global"],
      availableLanguage: ["English"],
      url: "https://behes.dev/discovery",
    },
    sameAs: ["https://github.com/behes-dev"],
    knowsAbout: [
      "One-Page Landing Page",
      "Custom Website",
      "Managed Hosting",
      "Professional Web Design",
      "Custom Website Design",
      "Small Business Websites",
      "Mobile-First Design",
    ],
    areaServed: {
      "@type": "Country",
      name: "United States",
    },
  };
}

export function generateLocalBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://behes.dev/#localbusiness",
    name: "behes.dev",
    image: "https://behes.dev/preview.png",
    url: "https://behes.dev",
    description:
      "Arthur Behesnilian, web designer building custom websites for small businesses and service brands. Offering One-Page Landing Page, Custom Website, and Managed Hosting services. Serving Pasadena, Los Angeles, and clients nationwide.",
    priceRange: "from $500",
    serviceType: [
      "One-Page Landing Page",
      "Custom Website",
      "Managed Hosting",
      "Custom Website Design",
      "Professional Web Design",
    ],
    areaServed: [
      {
        "@type": "City",
        name: "Pasadena",
        containedInPlace: {
          "@type": "State",
          name: "California",
        },
      },
      {
        "@type": "Country",
        name: "United States",
      },
    ],
    address: {
      "@type": "PostalAddress",
      addressLocality: "Pasadena",
      addressRegion: "CA",
      postalCode: "91104",
      addressCountry: "US",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 34.1478,
      longitude: -118.1445,
    },
  };
}

export function generatePasadenaLocalBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://behes.dev/pasadena-web-design/#localbusiness",
    name: "behes.dev",
    url: "https://behes.dev/pasadena-web-design",
    image: "https://behes.dev/preview.png",
    description: "Pasadena web designer. Custom websites built from scratch. Fast, clean, and professional.",
    priceRange: "from $500",
    serviceType: [
      "Web Design Pasadena",
      "Pasadena Web Design Services",
      "Pasadena Website Developer",
      "Freelance Web Designer Pasadena",
      "One-Page Landing Page",
      "Custom Website",
      "Managed Hosting",
    ],
    areaServed: {
      "@type": "Place",
      name: "Pasadena, CA",
      containedInPlace: {
        "@type": "State",
        name: "California",
        containedInPlace: {
          "@type": "Country",
          name: "United States",
        },
      },
    },
    address: {
      "@type": "PostalAddress",
      addressLocality: "Pasadena",
      addressRegion: "CA",
      postalCode: "91104",
      addressCountry: "US",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 34.1478,
      longitude: -118.1445,
    },
    sameAs: ["https://behes.dev"],
  };
}

export function generatePasadenaFAQSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Are you a freelance web designer in Pasadena?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. I'm Arthur Behesnilian, an independent web designer based in Pasadena. You work with me directly, start to finish.",
        },
      },
      {
        "@type": "Question",
        name: "What makes you different from other Pasadena website developers?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "I focus on building clean, professional websites that make a great first impression and are easy to navigate. An AI-accelerated workflow keeps turnaround fast without cutting quality.",
        },
      },
      {
        "@type": "Question",
        name: "Do you only work with Pasadena web design services?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "While I love working with local Pasadena businesses, I serve clients across California and nationwide.",
        },
      },
      {
        "@type": "Question",
        name: "What if I need something custom?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Get in touch at behes.dev/contact and tell me about your project. I'll get back to you within 24 hours to discuss options.",
        },
      },
    ],
  };
}

export function generatePersonSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": "https://behes.dev/#founder",
    name: "Arthur Behesnilian",
    jobTitle: "Web Designer",
    worksFor: {
      "@type": "Organization",
      "@id": "https://behes.dev/#organization",
      name: "behes.dev",
    },
    address: {
      "@type": "PostalAddress",
      addressLocality: "Pasadena",
      addressRegion: "CA",
      addressCountry: "US",
    },
    description:
      "Born and raised in Pasadena, CA. Professional web developer building custom websites for businesses and service brands.",
    knowsAbout: [
      "Web Development",
      "React",
      "Next.js",
      "User Experience Design",
      "Performance Optimization",
      "Custom Web Applications",
    ],
    sameAs: ["https://behes.dev/about"],
  };
}

export function generateAboutPageSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "@id": "https://behes.dev/about",
    url: "https://behes.dev/about",
    name: "About behes.dev - Arthur Behesnilian, Pasadena Web Designer",
    description:
      "Learn about Arthur Behesnilian, the web designer behind behes.dev. Custom websites for small businesses and service brands.",
    primaryImageOfPage: {
      "@type": "ImageObject",
      url: "https://behes.dev/founder-image.png",
      caption: "Arthur Behesnilian, web designer at behes.dev",
    },
    mainEntity: {
      "@type": "Person",
      "@id": "https://behes.dev/#founder",
    },
    publisher: {
      "@type": "Organization",
      "@id": "https://behes.dev/#organization",
    },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://behes.dev",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "About",
          item: "https://behes.dev/about",
        },
      ],
    },
  };
}

export function generateServicesPageSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://behes.dev/services",
    url: "https://behes.dev/services",
    name: "Web Design Services & Pricing - behes.dev",
    description:
      "Professional web design services from $500. One-Page Landing Pages, Custom Websites, and Managed Hosting for small businesses.",
    hasPart: [
      {
        "@type": "Service",
        "@id": "https://behes.dev/#landing-page-service",
      },
      {
        "@type": "Service",
        "@id": "https://behes.dev/#custom-website-service",
      },
      {
        "@type": "Service",
        "@id": "https://behes.dev/#managed-hosting-service",
      },
    ],
    publisher: {
      "@type": "Organization",
      "@id": "https://behes.dev/#organization",
    },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://behes.dev",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Services",
          item: "https://behes.dev/services",
        },
      ],
    },
  };
}

export function generateServicesFAQSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://behes.dev/services#faq",
    mainEntity: [
      {
        "@type": "Question",
        name: "What's the real investment for a professional website?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "100% transparent pricing: One-Page Landing Pages start at $500, Custom Websites start at $2,500, and Managed Hosting is $100/mo. Projects scale based on pages, features, and your goals.",
        },
      },
      {
        "@type": "Question",
        name: "Why shouldn't I just use Wix or Squarespace?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "DIY builders are great for hobbyists, but they come with limitations. They're slow, generic-looking, and harder to get found on Google. Plus, you're locked into their monthly fees forever. I build custom websites that load 3x faster, look professional, and perform better. While I offer optional hosting services, you own your website completely - you can host it anywhere you choose.",
        },
      },
      {
        "@type": "Question",
        name: "Will this actually help me get more leads or sales?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "A well-built website makes a strong first impression. Fast load times, clean design, working contact forms, and mobile optimization all help visitors take your business seriously. I set up analytics from day one so you can track results.",
        },
      },
      {
        "@type": "Question",
        name: "How long will my website take to build?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Timelines depend on the scope of your project. One-Page Landing Pages can launch in as little as a week. Custom Websites typically take 2-6 weeks depending on complexity. I believe in moving fast without sacrificing quality.",
        },
      },
      {
        "@type": "Question",
        name: "What if I need changes after my site launches?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Your website isn't set in stone. Small tweaks? I'm always happy to help. Regular updates? The care plans have you covered with monthly edits included. Major additions? I offer competitive hourly rates for additional work. You're never stuck with a site you can't update.",
        },
      },
      {
        "@type": "Question",
        name: "Do I actually own my website?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes, 100%. You own all the code, design, and content. If you choose my managed hosting, I handle the technical details while you maintain full ownership. Want to move to another host? No problem - you can take your website anywhere, anytime. No vendor lock-in, no surprise fees. It's your website, your choice.",
        },
      },
    ],
  };
}

export function generateServiceSchema() {
  return [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": "https://behes.dev/#landing-page-service",
      name: "One-Page Landing Page",
      description:
        "Perfect for founders who need a professional presence fast. A single-page website that establishes credibility and captures leads. Mobile-first responsive design.",
      provider: {
        "@type": "Organization",
        name: "behes.dev",
        url: "https://behes.dev",
      },
      areaServed: "US",
      category: "Web Design",
      serviceType: "Website Creation",
      offers: {
        "@type": "Offer",
        price: "500",
        priceCurrency: "USD",
        description: "Fast turnaround landing page",
        availability: "https://schema.org/InStock",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": "https://behes.dev/#custom-website-service",
      name: "Custom Website",
      description:
        "Built for businesses that need a full multi-page website. Custom design, multiple pages, and tailored functionality to match your brand and goals.",
      provider: {
        "@type": "Organization",
        name: "behes.dev",
        url: "https://behes.dev",
      },
      areaServed: "US",
      category: "Web Design",
      serviceType: "Website Creation",
      offers: {
        "@type": "Offer",
        price: "2500",
        priceCurrency: "USD",
        description: "Custom multi-page website",
        availability: "https://schema.org/InStock",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": "https://behes.dev/#managed-hosting-service",
      name: "Managed Hosting",
      description:
        "Reliable managed hosting for your website. I handle the technical details like updates, security, and uptime monitoring so you can focus on your business.",
      provider: {
        "@type": "Organization",
        name: "behes.dev",
        url: "https://behes.dev",
      },
      areaServed: "US",
      category: "Web Hosting",
      serviceType: "Managed Hosting",
      offers: {
        "@type": "Offer",
        price: "100",
        priceCurrency: "USD",
        description: "$100/mo managed hosting",
        availability: "https://schema.org/InStock",
      },
    },
  ];
}

export function generateFAQSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What makes behes.dev different from other web design agencies?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "I'm Arthur Behesnilian, an independent web designer focused on custom website design and speed optimization. A business-focused approach ensures modern, fast websites that rank well in Google and help grow your business.",
        },
      },
      {
        "@type": "Question",
        name: "Do you offer website speed optimization services?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. My website speed optimization includes comprehensive speed analysis, performance improvements, and ongoing monitoring. I specialize in making websites load faster to achieve higher Google rankings and better customer experience.",
        },
      },
      {
        "@type": "Question",
        name: "How long does a typical web design project take?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Project timelines vary by scope: One-Page Landing Pages can launch in as little as a week, and Custom Websites typically take 2-6 weeks. Timeline depends on complexity and custom requirements.",
        },
      },
      {
        "@type": "Question",
        name: "Do you work with clients outside of Pasadena?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Absolutely! While I'm based in Pasadena, CA, I serve clients nationwide. I work remotely with small businesses and companies of all sizes across the United States.",
        },
      },
      {
        "@type": "Question",
        name: "What's included in your website speed analysis?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "My website speed analysis includes comprehensive performance testing, loading speed evaluation, mobile optimization review, and detailed recommendations to improve your website's Google rankings and customer experience.",
        },
      },
    ],
  };
}

// ============================================
// CONTENT & ARTICLE SCHEMAS
// ============================================

interface BlogPostData {
  title: string;
  excerpt: string;
  content: string;
  publishedAt: Date;
  lastModified: Date;
  author: string;
  category: string;
  featuredImage?: string;
  wordCount: number;
  slug: string;
  tags: string[];
  schemaType?: string;
}

export function generateBlogPostSchema(
  post: BlogPostData,
  siteUrl: string = "https://behes.dev"
) {
  const schema = {
    "@context": "https://schema.org",
    "@type": post.schemaType || detectSchemaType(post.content),
    headline: post.title,
    description: post.excerpt,
    datePublished: post.publishedAt.toISOString(),
    dateModified: post.lastModified.toISOString(),
    author: {
      "@type": "Person",
      name: post.author,
      url: siteUrl,
    },
    publisher: {
      "@type": "Organization",
      name: "behes.dev",
      logo: {
        "@type": "ImageObject",
        url: `${siteUrl}/favicon.png`,
        width: 512,
        height: 512,
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${siteUrl}/blog/${post.category}/${post.slug}`,
    },
    wordCount: post.wordCount,
    articleSection: post.category,
    keywords: post.tags.join(", "),
  };

  // Add image if available
  if (post.featuredImage) {
    (schema as any).image = {
      "@type": "ImageObject",
      url: post.featuredImage.startsWith("http")
        ? post.featuredImage
        : `${siteUrl}${post.featuredImage}`,
      width: 1200,
      height: 630,
    };
  }

  // Add speakable for voice search optimization
  (schema as any).speakable = {
    "@type": "SpeakableSpecification",
    cssSelector: ["h1", ".excerpt", "h2"],
  };

  // Add additional properties based on schema type
  if (schema["@type"] === "HowTo") {
    const steps = extractHowToSteps(post.content);
    if (steps.length > 0) {
      (schema as any).step = steps;
    }
  } else if (schema["@type"] === "FAQPage") {
    const faqs = extractFAQs(post.content);
    if (faqs.length > 0) {
      (schema as any).mainEntity = faqs;
    }
  }

  return schema;
}

export function generateArticleListSchema(
  posts: BlogPostData[],
  siteUrl: string = "https://behes.dev"
) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: posts.map((post, index) => ({
      "@type": "ListItem",
      position: index + 1,
      url: `${siteUrl}/blog/${post.category}/${post.slug}`,
      name: post.title,
      description: post.excerpt,
    })),
  };
}

// ============================================
// PAGE-SPECIFIC SCHEMAS
// ============================================

export function generateWebPageSchema(
  title: string,
  description: string,
  url: string
) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": url,
    url: url,
    name: title,
    description: description,
    publisher: {
      "@type": "Organization",
      name: "behes.dev",
      url: "https://behes.dev",
    },
    isPartOf: {
      "@type": "WebSite",
      "@id": "https://behes.dev/#website",
      url: "https://behes.dev",
      name: "behes.dev",
      description:
        "Custom websites for small businesses by web designer Arthur Behesnilian",
      publisher: {
        "@type": "Organization",
        "@id": "https://behes.dev/#organization",
      },
    },
  };
}

export function generateCollectionPageSchema(
  title: string,
  description: string,
  url: string,
  itemCount?: number
) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": url,
    url: url,
    name: title,
    description: description,
    publisher: {
      "@type": "Organization",
      name: "behes.dev",
      url: "https://behes.dev",
    },
    isPartOf: {
      "@type": "WebSite",
      "@id": "https://behes.dev/#website",
    },
    ...(itemCount !== undefined && { numberOfItems: itemCount }),
  };
}

export function generatePricingPageSchema(
  siteUrl: string = "https://behes.dev"
) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${siteUrl}/pricing`,
    url: `${siteUrl}/pricing`,
    name: "Web Design Pricing & Packages - behes.dev",
    description:
      "Transparent pricing for professional web design services. One-Page Landing Page from $500, Custom Website from $2,500, and Managed Hosting at $100/mo.",
    publisher: {
      "@type": "Organization",
      name: "behes.dev",
      url: siteUrl,
    },
    isPartOf: {
      "@type": "WebSite",
      "@id": `${siteUrl}/#website`,
      url: siteUrl,
      name: "behes.dev",
    },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: siteUrl,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Pricing",
          item: `${siteUrl}/pricing`,
        },
      ],
    },
  };
}

export function generateDiscoveryPageSchema(
  siteUrl: string = "https://behes.dev"
) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${siteUrl}/discovery`,
    url: `${siteUrl}/discovery`,
    name: "Project Discovery Questionnaire - behes.dev",
    description:
      "Start your web design project with my discovery questionnaire. Help me understand your business needs and goals to create the perfect website solution.",
    publisher: {
      "@type": "Organization",
      name: "behes.dev",
      url: siteUrl,
    },
    isPartOf: {
      "@type": "WebSite",
      "@id": `${siteUrl}/#website`,
    },
    potentialAction: {
      "@type": "Action",
      name: "Complete Discovery Questionnaire",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${siteUrl}/discovery`,
      },
    },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: siteUrl,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Discovery",
          item: `${siteUrl}/discovery`,
        },
      ],
    },
  };
}

export function generateContactPageSchema(
  siteUrl: string = "https://behes.dev"
) {
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "@id": `${siteUrl}/contact`,
    url: `${siteUrl}/contact`,
    name: "Contact Us | behes.dev",
    description:
      "Get in touch about your web design project. Custom websites built from scratch for businesses and service brands. Based in Pasadena, serving clients nationwide.",
    publisher: {
      "@type": "Organization",
      name: "behes.dev",
      url: siteUrl,
    },
    isPartOf: {
      "@type": "WebSite",
      "@id": `${siteUrl}/#website`,
    },
    mainEntity: {
      "@type": "Organization",
      "@id": `${siteUrl}/#organization`,
    },
    potentialAction: {
      "@type": "CommunicateAction",
      name: "Contact Us",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${siteUrl}/contact`,
        actionPlatform: ["http://schema.org/DesktopWebPlatform", "http://schema.org/MobileWebPlatform"],
      },
    },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: siteUrl,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Contact",
          item: `${siteUrl}/contact`,
        },
      ],
    },
  };
}

export function generateContactFAQSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://behes.dev/contact#faq",
    mainEntity: [
      {
        "@type": "Question",
        name: "How do I get started with my web design project?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Simply fill out my contact form with your project details. I'll get back to you within 24 hours to schedule a strategy call and discuss your goals, timeline, and budget.",
        },
      },
      {
        "@type": "Question",
        name: "What happens after I submit my contact form?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "I'll review your project details and respond within 24 hours. If we're a good fit, I'll schedule a strategy call to dive deeper into your goals and discuss the best package for your needs. Most projects start within 48 hours of our initial call.",
        },
      },
      {
        "@type": "Question",
        name: "Do you work with clients outside of Pasadena?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Absolutely! While I'm based in Pasadena, CA, I serve clients nationwide. All work is done remotely, and I've successfully completed projects for businesses across the United States.",
        },
      },
      {
        "@type": "Question",
        name: "How quickly can you start my project?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "I maintain a selective client roster to ensure every website receives dedicated attention and craftsmanship. While most approved projects begin within 48-72 hours of our strategy call, I only take on a limited number of clients at a time. This approach guarantees you'll receive my full focus and a truly exceptional website, not a rushed job.",
        },
      },
      {
        "@type": "Question",
        name: "What information should I include in my contact form?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Include your business name, what type of website you need, your timeline, and any specific features or goals you have in mind. The more details you provide, the better I can tailor the initial consultation to your needs.",
        },
      },
    ],
  };
}

export function generatePortfolioPageSchema(
  projects: any[] = [],
  siteUrl: string = "https://behes.dev"
) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${siteUrl}/portfolio`,
    url: `${siteUrl}/portfolio`,
    name: "Portfolio | Web Design Projects & Case Studies - behes.dev",
    description:
      "Explore my portfolio of custom websites built for real businesses. From local services to creative professionals. Fast, hand-coded sites built to last.",
    publisher: {
      "@type": "Organization",
      "@id": `${siteUrl}/#organization`,
      name: "behes.dev",
      url: siteUrl,
    },
    isPartOf: {
      "@type": "WebSite",
      "@id": `${siteUrl}/#website`,
      url: siteUrl,
      name: "behes.dev",
    },
    numberOfItems: projects.length,
    hasPart: projects.map((project, index) => ({
      "@type": "CreativeWork",
      "@id": `${siteUrl}/portfolio#project-${project.id || index}`,
      name: project.title,
      description: project.description,
      url: project.link,
      image: project.image ? `${siteUrl}${project.image}` : undefined,
      creator: {
        "@type": "Organization",
        "@id": `${siteUrl}/#organization`,
      },
      keywords: project.stack ? project.stack.join(", ") : undefined,
      isBasedOn: project.caseStudySlug
        ? `${siteUrl}/case-studies/${project.caseStudySlug}`
        : undefined,
    })),
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: siteUrl,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Portfolio",
          item: `${siteUrl}/portfolio`,
        },
      ],
    },
    mainEntity: {
      "@type": "ItemList",
      itemListElement: projects.map((project, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url: project.link,
        name: project.title,
      })),
    },
  };
}

export function generateCaseStudySchema(
  caseStudy: {
    title: string;
    description: string;
    client: string;
    technologies: string[];
    url?: string;
    image?: string;
    slug: string;
  },
  siteUrl: string = "https://behes.dev"
) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${siteUrl}/case-studies/${caseStudy.slug}`,
    url: `${siteUrl}/case-studies/${caseStudy.slug}`,
    name: caseStudy.title,
    description: caseStudy.description,
    publisher: {
      "@type": "Organization",
      "@id": `${siteUrl}/#organization`,
      name: "behes.dev",
      url: siteUrl,
    },
    isPartOf: {
      "@type": "WebSite",
      "@id": `${siteUrl}/#website`,
    },
    about: {
      "@type": "CreativeWork",
      name: caseStudy.client,
      description: caseStudy.description,
      url: caseStudy.url,
      keywords: caseStudy.technologies.join(", "),
      creator: {
        "@type": "Organization",
        "@id": `${siteUrl}/#organization`,
      },
    },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: siteUrl,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Portfolio",
          item: `${siteUrl}/portfolio`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: caseStudy.client,
          item: `${siteUrl}/case-studies/${caseStudy.slug}`,
        },
      ],
    },
  };
}

export function generateBlogListingPageSchema(
  postCount: number,
  siteUrl: string = "https://behes.dev"
) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${siteUrl}/blog`,
    url: `${siteUrl}/blog`,
    name: "Blog | Web Development Insights & Tips - behes.dev",
    description:
      "Expert insights on web development and building successful online businesses. Learn from my experience building high-performance websites.",
    publisher: {
      "@type": "Organization",
      name: "behes.dev",
      url: siteUrl,
    },
    isPartOf: {
      "@type": "WebSite",
      "@id": `${siteUrl}/#website`,
    },
    numberOfItems: postCount,
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: siteUrl,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Blog",
          item: `${siteUrl}/blog`,
        },
      ],
    },
  };
}

// ============================================
// NAVIGATION SCHEMAS
// ============================================

export function generateBreadcrumbSchema(
  items:
    | Array<{ name: string; url: string }>
    | {
        category: string;
        postTitle: string;
        slug: string;
        siteUrl?: string;
      }
) {
  // Handle blog post breadcrumb (backward compatibility)
  if ("category" in items && "postTitle" in items && "slug" in items) {
    const siteUrl = items.siteUrl || "https://behes.dev";
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: siteUrl,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Blog",
          item: `${siteUrl}/blog`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: items.category
            .replace("-", " ")
            .replace(/\b\w/g, (l) => l.toUpperCase()),
          item: `${siteUrl}/blog?category=${items.category}`,
        },
        {
          "@type": "ListItem",
          position: 4,
          name: items.postTitle,
          item: `${siteUrl}/blog/${items.category}/${items.slug}`,
        },
      ],
    };
  }

  // Handle generic breadcrumb
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: (items as Array<{ name: string; url: string }>).map(
      (item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        item: item.url,
      })
    ),
  };
}

// ============================================
// HELPER FUNCTIONS
// ============================================

function detectSchemaType(content: string): string {
  const contentLower = content.toLowerCase();

  // Check for How-To indicators
  if (
    /<ol>/.test(content) &&
    /\b(step|first|then|next|finally|how to)\b/i.test(content)
  ) {
    return "HowTo";
  }

  // Check for FAQ indicators
  if (
    /(<h[2-6][^>]*>.*\?.*<\/h[2-6]>)/gi.test(content) &&
    (contentLower.includes("faq") ||
      contentLower.includes("frequently asked") ||
      contentLower.includes("question"))
  ) {
    return "FAQPage";
  }

  // Check for Recipe indicators
  if (
    /\b(ingredients|recipe|cooking|baking)\b/i.test(content) &&
    /\b(minutes|hours|prep time|cook time)\b/i.test(content)
  ) {
    return "Recipe";
  }

  // Default to BlogPosting
  return "BlogPosting";
}

function extractHowToSteps(content: string): any[] {
  const steps: any[] = [];
  const listMatch = content.match(/<ol>(.*?)<\/ol>/s);

  if (listMatch) {
    const items = listMatch[1].match(/<li>(.*?)<\/li>/gs);
    if (items) {
      items.forEach((item, index) => {
        const text = item.replace(/<[^>]*>/g, "").trim();
        steps.push({
          "@type": "HowToStep",
          position: index + 1,
          name: `Step ${index + 1}`,
          text: text,
        });
      });
    }
  }

  return steps;
}

function extractFAQs(content: string): any[] {
  const faqs: any[] = [];
  const questionPattern = /<h[2-6][^>]*>(.*?\?.*?)<\/h[2-6]>\s*<p>(.*?)<\/p>/gi;
  let match;

  while ((match = questionPattern.exec(content)) !== null) {
    faqs.push({
      "@type": "Question",
      name: match[1].replace(/<[^>]*>/g, "").trim(),
      acceptedAnswer: {
        "@type": "Answer",
        text: match[2].replace(/<[^>]*>/g, "").trim(),
      },
    });
  }

  return faqs;
}
